import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Copy, Pause, Play, Archive, Send, Pencil, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, img, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OFFER_TYPES, fmtDate, offerText } from "@/lib/format";
import { PageHead } from "@/components/portal/PortalShell";
import { Field, NativeSelect, StatusBadge, Empty, Countdown } from "@/components/site/bits";
import { ImageUploader } from "@/components/site/ImageUploader";

const STATUSES = ["", "active", "paused", "pending_review", "draft", "expired", "archived", "rejected"];

function DuplicateDialog({ deal, onClose, onDone }) {
  const { slots_used, subscription } = useAuth();
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/seller/branches").then((r) => setBranches(r.data.filter((b) => b.active)));
  }, []);
  const run = async (publish) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/deals/${deal.id}/duplicate`, { branch_ids: sel, publish });
      toast.success(`${data.created} deal(s) created${publish ? " and published" : " as drafts"}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="duplicate-dialog">
        <DialogHeader><DialogTitle className="font-heading">Duplicate to branches</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Each branch gets its own deal record and uses one active slot when published.</p>
        <div className="max-h-60 overflow-auto space-y-2">
          {branches.map((b) => (
            <label key={b.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200">
              <Checkbox data-testid={`dup-branch-${b.id}`} checked={sel.includes(b.id)} onCheckedChange={(c) => setSel(c ? [...sel, b.id] : sel.filter((x) => x !== b.id))} />
              <span className="text-sm"><b>{b.name}</b><br /><span className="text-slate-500">{b.address}</span></span>
            </label>
          ))}
        </div>
        <p data-testid="dup-slots-required" className="text-sm font-semibold">Slots required: {sel.length} · Available: {subscription ? Math.max(0, subscription.slots - slots_used) : 0}</p>
        <div className="flex gap-2">
          <button data-testid="dup-draft-button" disabled={!sel.length || busy} onClick={() => run(false)} className="flex-1 h-11 rounded-xl border border-slate-200 font-bold">Save as drafts</button>
          <button data-testid="dup-publish-button" disabled={!sel.length || busy} onClick={() => run(true)} className="flex-1 h-11 rounded-xl bg-red-500 text-white font-bold">Publish all</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SellerDeals() {
  const { user, refresh } = useAuth();
  const owner = user.merchant_role === "owner";
  const [status, setStatus] = useState("");
  const [deals, setDeals] = useState(null);
  const [dup, setDup] = useState(null);
  const load = () => api.get("/seller/deals", { params: { status: status || undefined } }).then((r) => setDeals(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  const act = async (d, action) => {
    try {
      await api.post(`/seller/deals/${d.id}/${action}`);
      toast.success(`Deal ${action === "publish" ? "published" : action + "d"}`);
      load();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHead title="My Deals" sub="Each published deal uses one active slot. Drafts are free.">
        {owner && <Link to="/seller/deals/new" data-testid="new-deal-button" className="px-5 h-10 inline-flex items-center rounded-full bg-red-500 text-white font-bold text-sm">+ Create deal</Link>}
      </PageHead>
      <div className="flex gap-2 flex-wrap mb-4">
        {STATUSES.map((s) => <button key={s || "all"} data-testid={`deals-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-bold border capitalize ${status === s ? "bg-slate-900 text-white" : "bg-white border-slate-200"}`}>{(s || "all").replace("_", " ")}</button>)}
      </div>
      {!deals ? <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" /> : deals.length === 0 ? <Empty testid="deals-empty" title="No deals yet" /> : (
        <div className="space-y-3">
          {deals.map((d) => (
            <div key={d.id} data-testid={`seller-deal-${d.id}`} className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-col sm:flex-row gap-4">
              {d.images?.[0] ? <img src={img(d.images[0])} alt="" className="w-full sm:w-28 h-28 rounded-xl object-cover" /> : <div className="w-28 h-28 rounded-xl bg-slate-100" />}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap"><StatusBadge status={d.status} testid={`deal-status-${d.id}`} /><span className="text-xs font-bold text-red-500">{offerText(d)}</span></div>
                <p className="font-heading font-bold text-slate-900">{d.title}</p>
                <p className="text-xs text-slate-500">{d.branch_name} · {d.duration_hours}h · {d.expires_at ? `Ends ${fmtDate(d.expires_at, d.timezone)}` : "Not published"}</p>
                <p className="text-xs text-slate-500">{d.views_count} views · {d.claims_count} claims · {d.redemptions_count} redeemed · {d.shares_count} shares</p>
                {d.status === "active" && <Countdown to={d.expires_at} />}
              </div>
              {owner && (
                <div className="flex sm:flex-col flex-wrap gap-1.5 sm:w-36">
                  {["draft", "rejected"].includes(d.status) && <button data-testid={`publish-${d.id}`} onClick={() => act(d, "publish")} className="btn-act bg-red-500 text-white"><Send className="w-3.5 h-3.5" />Publish</button>}
                  {d.status === "active" && <button data-testid={`pause-${d.id}`} onClick={() => act(d, "pause")} className="btn-act"><Pause className="w-3.5 h-3.5" />Pause</button>}
                  {d.status === "paused" && <button data-testid={`resume-${d.id}`} onClick={() => act(d, "resume")} className="btn-act"><Play className="w-3.5 h-3.5" />Resume</button>}
                  {!["expired", "archived"].includes(d.status) && <Link data-testid={`edit-${d.id}`} to={`/seller/deals/${d.id}/edit`} className="btn-act"><Pencil className="w-3.5 h-3.5" />Edit</Link>}
                  <button data-testid={`duplicate-${d.id}`} onClick={() => setDup(d)} className="btn-act"><Copy className="w-3.5 h-3.5" />Duplicate</button>
                  {d.status !== "archived" && <button data-testid={`archive-${d.id}`} onClick={() => act(d, "archive")} className="btn-act"><Archive className="w-3.5 h-3.5" />Archive</button>}
                  {d.status === "active" && <a href={`/deals/${d.slug}`} target="_blank" rel="noreferrer" className="btn-act"><ExternalLink className="w-3.5 h-3.5" />View</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {dup && <DuplicateDialog deal={dup} onClose={() => setDup(null)} onDone={() => { setDup(null); load(); refresh(); }} />}
    </div>
  );
}

const EMPTY = { title: "", description: "", images: [], category_id: "", branch_id: "", offer_type: "percentage", offer_label: "", original_price: "", offer_price: "", discount_value: "", start_at: "", duration_hours: 24, terms: "", redemption_instructions: "", max_claims: "" };

export function DealForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh, subscription, slots_used, merchant } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [status, setStatus] = useState("draft");
  const [cats, setCats] = useState([]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/seller/branches").then((r) => setBranches(r.data.filter((b) => b.active)));
    if (id) api.get(`/seller/deals/${id}`).then(({ data }) => {
      setStatus(data.status);
      setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])), start_at: "" });
    });
    else setF((x) => ({ ...x, category_id: merchant?.category_id || "" }));
  }, [id, merchant]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => (v === "" || v == null ? null : +v);
  const locked = id && !["draft", "rejected"].includes(status);

  const save = async (publish) => {
    if (f.images.length === 0) return toast.error("Add at least one photo");
    setBusy(true);
    const body = { ...f, original_price: num(f.original_price), offer_price: num(f.offer_price), discount_value: num(f.discount_value), max_claims: num(f.max_claims), duration_hours: +f.duration_hours, start_at: f.start_at ? new Date(f.start_at).toISOString() : null };
    try {
      if (id) {
        await api.put(`/seller/deals/${id}`, body);
        if (publish) await api.post(`/seller/deals/${id}/publish`);
      } else await api.post("/seller/deals", { ...body, publish });
      toast.success(publish ? "Deal published" : "Deal saved");
      refresh();
      nav("/seller/deals");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHead title={id ? "Edit deal" : "Create deal"} sub={subscription ? `${Math.max(0, subscription.slots - slots_used)} of ${subscription.slots} slots available · Publishing uses 1 slot` : "No active subscription — you can save drafts."} />
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <Field label="Photos (up to 5, first is the cover)"><ImageUploader value={f.images} onChange={(images) => setF({ ...f, images })} testid="deal-images" /></Field>
        <Field label="Deal title"><Input data-testid="deal-title-input" value={f.title} onChange={set("title")} maxLength={120} /></Field>
        <Field label="Description"><Textarea data-testid="deal-description-input" rows={4} value={f.description} onChange={set("description")} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category"><NativeSelect data-testid="deal-category-select" value={f.category_id} onChange={set("category_id")}><option value="">Select…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></Field>
          <Field label="Participating branch" hint="One deal = one branch. Use Duplicate for more branches."><NativeSelect data-testid="deal-branch-select" disabled={locked} value={f.branch_id} onChange={set("branch_id")}><option value="">Select…</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</NativeSelect></Field>
          <Field label="Offer type"><NativeSelect data-testid="deal-offer-type-select" value={f.offer_type} onChange={set("offer_type")}>{Object.entries(OFFER_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></Field>
          <Field label="Offer label (optional)" hint="e.g. Buy 1 Get 1, Free dessert"><Input data-testid="deal-offer-label-input" value={f.offer_label} onChange={set("offer_label")} /></Field>
          <Field label="Original price (₹, optional)"><Input data-testid="deal-original-price-input" type="number" min="0" value={f.original_price} onChange={set("original_price")} /></Field>
          <Field label="Offer price (₹, optional)"><Input data-testid="deal-offer-price-input" type="number" min="0" value={f.offer_price} onChange={set("offer_price")} /></Field>
          {["percentage", "fixed"].includes(f.offer_type) && <Field label={f.offer_type === "percentage" ? "Discount %" : "Discount amount (₹)"}><Input data-testid="deal-discount-input" type="number" min="0" value={f.discount_value} onChange={set("discount_value")} /></Field>}
          <Field label="Duration"><NativeSelect data-testid="deal-duration-select" disabled={locked} value={f.duration_hours} onChange={set("duration_hours")}>{[24, 36, 48, 72].map((h) => <option key={h} value={h}>{h} hours</option>)}</NativeSelect></Field>
          <Field label="Start (optional, default: on publish)"><Input data-testid="deal-start-input" disabled={locked} type="datetime-local" value={f.start_at} onChange={set("start_at")} /></Field>
          <Field label="Max claims (optional)"><Input data-testid="deal-max-claims-input" type="number" min="1" value={f.max_claims} onChange={set("max_claims")} /></Field>
        </div>
        <Field label="Terms & conditions"><Textarea data-testid="deal-terms-input" value={f.terms} onChange={set("terms")} /></Field>
        <Field label="Redemption instructions"><Textarea data-testid="deal-instructions-input" value={f.redemption_instructions} onChange={set("redemption_instructions")} /></Field>
        <div className="flex flex-wrap gap-3">
          <button data-testid="deal-save-draft" disabled={busy} onClick={() => save(false)} className="px-6 h-11 rounded-xl border border-slate-200 font-bold">{locked ? "Save changes" : "Save draft"}</button>
          {!locked && <button data-testid="deal-publish" disabled={busy} onClick={() => save(true)} className="px-6 h-11 rounded-xl bg-red-500 text-white font-bold flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Publish (uses 1 slot)</button>}
        </div>
      </div>
    </div>
  );
}
